import { getRecentEdit, putRecentEdit } from '@/core/persistence/recentEdits'
import { recordFromState } from '@/core/persistence/serialize'
import { AUTOSAVE_DEBOUNCE_MS, PersistenceStatus } from '@/core/persistence/types'
import { PersistenceReadError } from '@/core/persistence/errors'
import { useEditSession } from '@/core/store/editSession'
import { createThumbnail } from '@/core/persistence/thumbnail'

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error('Could not autosave the current edit.')
}

export function startAutosave(onError: (error: Error) => void): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null
  let errorReported = false
  let thumbnailDirty = false

  const unsubscribe = useEditSession.subscribe((state, previous) => {
    const documentChanged = state.document !== previous.document
    const changed = documentChanged
      || state.selectedNodeId !== previous.selectedNodeId
      || state.exportOptions !== previous.exportOptions
      || state.persistedEditId !== previous.persistedEditId
    if (!changed || !state.document || !state.persistedEditId || state.persistenceStatus === PersistenceStatus.Disabled) return
    if (documentChanged) thumbnailDirty = true
    if (timer !== null) clearTimeout(timer)
    state.setPersistenceStatus(PersistenceStatus.Saving)
    timer = setTimeout(() => {
      timer = null
      void (async () => {
        try {
          const current = useEditSession.getState()
          const id = current.persistedEditId
          if (!current.document || !id) throw new Error('Autosave requires a loaded persisted edit')
          const existing = await getRecentEdit(id)
          if (!existing) throw new PersistenceReadError(`Saved edit "${id}" was not found.`)
          const thumbnail = thumbnailDirty
            ? await createThumbnail(current.document, current.sources)
            : existing.thumbnail
          await putRecentEdit(recordFromState(current, thumbnail, { ...existing, isOpen: true }))
          const latest = useEditSession.getState()
          if (latest.persistedEditId === id && latest.document === current.document) {
            thumbnailDirty = false
            useEditSession.getState().setPersistenceStatus(PersistenceStatus.Saved)
          }
        } catch (error) {
          useEditSession.getState().setPersistenceStatus(PersistenceStatus.Disabled)
          if (!errorReported) {
            errorReported = true
            onError(asError(error))
          }
        }
      })()
    }, AUTOSAVE_DEBOUNCE_MS)
  })

  return () => {
    if (timer !== null) clearTimeout(timer)
    unsubscribe()
  }
}
